import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { postData } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

/**
 * Centralized logout component that handles all logout scenarios
 * Provides consistent UX with loading states and error handling
 */
const Logout: React.FC = () => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { logout } = useAuth();
  const [status, setStatus] = useState<'logging-out' | 'error' | 'success'>('logging-out');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const performLogout = async () => {
      try {
        // 1. Call v2 server logout endpoint (clears HTTP-only cookies)
        await postData('/api/v2/auth/logout', {});
        
        // 2. Call AuthContext logout (clears local storage and state)
        await logout();

        // 3. Check if user logged in via SSO using stored auth method
        const authMethod = localStorage.getItem('authMethod');
        const accounts = instance.getAllAccounts();
        const isSSO = authMethod === 'sso' || accounts.length > 0;

        // Only logout from Microsoft MSAL if user actually logged in via SSO
        if (isSSO) {
          try {
            await instance.logoutPopup({
              mainWindowRedirectUri: window.location.origin + '/login'
            });
          } catch (msalError) {
            console.warn('MSAL logout failed, continuing with redirect:', msalError);
            // Continue with navigation even if MSAL logout fails
          }
        }

        setStatus('success');
        
        // 4. Navigate to login after brief success indication
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 1500);

      } catch (error) {
        console.error('Logout failed:', error);
        setErrorMessage('Logout failed. Redirecting to login...');
        setStatus('error');
        
        // Even if logout fails, clear what we can and redirect
        try {
          await logout();
        } catch (contextError) {
          console.error('AuthContext logout failed:', contextError);
        }
        
        // Redirect after showing error
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    performLogout();
  }, [instance, navigate, logout]);

  const renderContent = () => {
    switch (status) {
      case 'logging-out':
        return (
          <>
            <CircularProgress />
            <Typography variant="h6">Logging out...</Typography>
            <Typography variant="body2" color="text.secondary">
              Clearing session and redirecting...
            </Typography>
          </>
        );
      
      case 'success':
        return (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Logged out successfully!
            </Alert>
            <Typography variant="h6">Redirecting to login...</Typography>
          </>
        );
      
      case 'error':
        return (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
            <CircularProgress size={24} />
            <Typography variant="body2">Redirecting...</Typography>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={2}
      sx={{ p: 4 }}
    >
      {renderContent()}
    </Box>
  );
};

export default Logout;